'use client';

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { LiveblocksProvider, RoomProvider, useMyPresence, useOthers, useUpdateMyPresence } from '@liveblocks/react';
import { createClient } from '@liveblocks/client';
import { createCollaborator, type CollaborationPresence, type CollaborationUserMeta } from './presence';

export type { CollaborationPresence };

interface PresenceProviderProps {
  appId: string;
  pageId: string;
  children: ReactNode;
}

interface CollaborationIdentityContextValue {
  enabled: boolean;
  me: CollaborationUserMeta;
}

interface PresenceActionsContextValue {
  updatePresence: (presence: Partial<CollaborationPresence>) => void;
  others: CollaborationPresence[];
}

const CollaborationIdentityContext = createContext<CollaborationIdentityContextValue | null>(null);
const PresenceActionsContext = createContext<PresenceActionsContextValue>({
  updatePresence: () => undefined,
  others: [],
});

const rawLiveblocksKey = import.meta.env.VITE_LIVEBLOCKS_PUBLIC_KEY as string | undefined;
const isValidLiveblocksKey = typeof rawLiveblocksKey === 'string' && (rawLiveblocksKey.startsWith('pk_live_') || rawLiveblocksKey.startsWith('pk_test_'));

const liveblocksClient = isValidLiveblocksKey
  ? createClient({ publicApiKey: rawLiveblocksKey! })
  : null;

const INITIAL_PRESENCE: CollaborationPresence = {
  cursor: null,
  selectedRowId: null,
  activeField: null,
};

/** Inner component — only mounts when RoomProvider is in the tree */
function LivePresenceActionsConnector({ children }: { children: ReactNode }) {
  const updateMyPresence = useUpdateMyPresence<CollaborationPresence>();
  const rawOthers = useOthers();

  const updatePresence = useCallback(
    (presence: Partial<CollaborationPresence>) => { updateMyPresence(presence); },
    [updateMyPresence],
  );

  const others = useMemo(
    () => rawOthers.map(o => o.presence as CollaborationPresence),
    [rawOthers],
  );

  return (
    <PresenceActionsContext.Provider value={{ updatePresence, others }}>
      {children}
    </PresenceActionsContext.Provider>
  );
}

export function PresenceProvider({ appId, pageId, children }: PresenceProviderProps) {
  const roomId = `${appId}:${pageId}`;
  const me = useMemo(() => createCollaborator(roomId), [roomId]);

  if (!liveblocksClient) {
    return (
      <CollaborationIdentityContext.Provider value={{ enabled: false, me }}>
        {children}
      </CollaborationIdentityContext.Provider>
    );
  }

  return (
    <LiveblocksProvider client={liveblocksClient}>
      <RoomProvider id={roomId} initialPresence={INITIAL_PRESENCE}>
        <CollaborationIdentityContext.Provider value={{ enabled: true, me }}>
          <LivePresenceActionsConnector>
            {children}
          </LivePresenceActionsConnector>
        </CollaborationIdentityContext.Provider>
      </RoomProvider>
    </LiveblocksProvider>
  );
}

export function useCollaborationIdentity(): CollaborationIdentityContextValue {
  const context = useContext(CollaborationIdentityContext);
  if (!context) {
    throw new Error('useCollaborationIdentity must be used within PresenceProvider');
  }
  return context;
}

export function usePresenceActions(): PresenceActionsContextValue {
  return useContext(PresenceActionsContext);
}

export function useSelectedRowPresence() {
  const [, updateMyPresence] = useMyPresence<CollaborationPresence>();
  return (selectedRowId: string | null) => {
    updateMyPresence({ selectedRowId });
  };
}
